import Client from "@/services/client";

export const ONBOARDING_COMPLETED_KEY = "gamefik_onboarding_completed";
/**
 * Payload de uma conclusão que o backend ainda não confirmou. Fica no
 * localStorage até o flush conseguir persistir (ver persistOnboardingCompletion).
 *
 * ESCOPO DE SESSÃO POR DESIGN: `clearLocalStoragePreservingLocale()` (login)
 * roda `localStorage.clear()` e leva esta chave junto. Isso é intencional —
 * preservá-la faria a conclusão de um professor ser reenviada para a conta de
 * quem logasse depois no mesmo navegador (escola com máquina compartilhada).
 * A janela coberta é a que importa na prática: entre concluir o wizard e
 * encerrar a sessão, o OnboardingGate remonta a cada carregamento de página e
 * reenvia até o servidor confirmar.
 */
export const ONBOARDING_PENDING_SYNC_KEY = "gamefik_onboarding_pending_sync";

const COMPLETE_PATH = "onboarding/professor/complete";
const RETRY_DELAY_MS = 1200;

export type OnboardingCompletionPayload = Record<string, unknown>;

type MinimalStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type CompletionRequest = (args: {
  method: "PUT";
  path: string;
  data: OnboardingCompletionPayload;
}) => Promise<unknown>;

export interface OnboardingCompletionDeps {
  /** Injetável nos testes. Default: Client.authenticatedRequest (axios — LANÇA em erro). */
  request?: CompletionRequest;
  /** Injetável nos testes. Default: localStorage (ou null em SSR). */
  storage?: MinimalStorage | null;
  /** Injetável nos testes para não esperar o backoff real. */
  delay?: (ms: number) => Promise<void>;
}

function defaultStorage(): MinimalStorage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    // Navegação privada / storage bloqueado.
    return null;
  }
}

const defaultRequest: CompletionRequest = (args) => Client.authenticatedRequest(args);
const defaultDelay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function readKey(storage: MinimalStorage | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function writeKey(storage: MinimalStorage | null, key: string, value: string) {
  if (!storage) return;
  try {
    storage.setItem(key, value);
  } catch {
    /* noop */
  }
}

function deleteKey(storage: MinimalStorage | null, key: string) {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    /* noop */
  }
}

/** Marca local de conclusão — cache de leitura rápida, NUNCA a única fonte da verdade. */
export function markOnboardingCompletedLocally(storage: MinimalStorage | null = defaultStorage()) {
  writeKey(storage, ONBOARDING_COMPLETED_KEY, "true");
}

export function hasLocalOnboardingCompletion(storage: MinimalStorage | null = defaultStorage()) {
  return Boolean(readKey(storage, ONBOARDING_COMPLETED_KEY));
}

export function readPendingOnboardingCompletion(
  storage: MinimalStorage | null = defaultStorage(),
): OnboardingCompletionPayload | null {
  const raw = readKey(storage, ONBOARDING_PENDING_SYNC_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as OnboardingCompletionPayload) : null;
  } catch {
    // Payload corrompido: descarta para não tentar para sempre.
    deleteKey(storage, ONBOARDING_PENDING_SYNC_KEY);
    return null;
  }
}

export function clearPendingOnboardingCompletion(storage: MinimalStorage | null = defaultStorage()) {
  deleteKey(storage, ONBOARDING_PENDING_SYNC_KEY);
}

/**
 * Persiste a conclusão do onboarding do professor no backend (2 tentativas com
 * backoff). O handoff continua NON-BLOCKING por design — a marca local é escrita
 * de qualquer jeito para o usuário não travar.
 *
 * A diferença para o código anterior (que vivia duplicado nas duas páginas de
 * wizard): uma falha total não é mais esquecida. O payload fica em
 * `gamefik_onboarding_pending_sync` e `flushPendingOnboardingCompletion()` — que
 * o OnboardingGate chama a cada montagem de professor global — tenta de novo até
 * o servidor confirmar.
 *
 * Bug real (2026-07-29): os dois wizards escreviam a flag local mesmo com o PUT
 * falhando 2x. O dispositivo ficava "concluído" para sempre na UI e "não
 * concluído" para sempre no banco; o próximo login limpa o localStorage
 * (clearLocalStoragePreservingLocale) e o professor caía no wizard do zero, sem
 * nenhuma forma de convergir sozinho.
 */
export async function persistOnboardingCompletion(
  payload: OnboardingCompletionPayload,
  deps: OnboardingCompletionDeps = {},
): Promise<boolean> {
  const request = deps.request ?? defaultRequest;
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;
  const delay = deps.delay ?? defaultDelay;

  let persisted = false;
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      await request({ method: "PUT", path: COMPLETE_PATH, data: payload });
      persisted = true;
      break;
    } catch (err) {
      if (attempt === 2) {
        console.error("[onboarding-completion] persistência falhou, agendando retry:", err);
      } else {
        await delay(RETRY_DELAY_MS);
      }
    }
  }

  if (persisted) {
    clearPendingOnboardingCompletion(storage);
  } else {
    writeKey(storage, ONBOARDING_PENDING_SYNC_KEY, JSON.stringify(payload));
  }
  markOnboardingCompletedLocally(storage);
  return persisted;
}

/**
 * Retry silencioso e de melhor esforço de uma conclusão que nunca chegou ao
 * backend. Nunca bloqueia nem mostra erro; devolve true só quando o servidor
 * confirmou (aí o chamador pode invalidar o cache de users/info).
 */
export async function flushPendingOnboardingCompletion(
  deps: OnboardingCompletionDeps = {},
): Promise<boolean> {
  const request = deps.request ?? defaultRequest;
  const storage = deps.storage === undefined ? defaultStorage() : deps.storage;

  const payload = readPendingOnboardingCompletion(storage);
  if (!payload) return false;

  try {
    await request({ method: "PUT", path: COMPLETE_PATH, data: payload });
    clearPendingOnboardingCompletion(storage);
    return true;
  } catch (err) {
    console.error("[onboarding-completion] flush em segundo plano ainda falhando:", err);
    return false;
  }
}
